<template>
  <div class="spawn-reference-data animated fadeIn">
    <b-card no-body class="reference-toolbar-card">
      <div class="reference-toolbar">
        <div>
          <div class="reference-title-row">
            <i class="fa fa-table text-info"></i>
            <strong>Reference Data</strong>
          </div>
          <div class="text-muted small">Spawn Editor lookup data</div>
        </div>

        <div class="reference-actions">
          <span v-if="saving" class="text-muted small">
            <i class="fa fa-refresh fa-spin mr-1"></i>
            Saving...
          </span>
          <b-button size="sm" variant="outline-info" :disabled="loading" @click="refresh">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </b-button>
        </div>
      </div>
    </b-card>

    <b-tabs v-model="activeTabIndex" pills card lazy class="reference-tabs" nav-class="reference-tab-nav">
      <b-tab v-for="config in configs" :key="config.key" :title="config.title">
        <div class="reference-section-bar">
          <b-input-group size="sm" class="reference-search">
            <b-input-group-prepend is-text class="bg-dark border-secondary">
              <i class="fa fa-search small"></i>
            </b-input-group-prepend>
            <b-form-input
              v-model.trim="searchInputs[config.key]"
              :placeholder="'Search ' + config.title + '...'"
              class="bg-dark text-white border-secondary"
              @input="queueSearch(config.key, $event)"
            ></b-form-input>
          </b-input-group>

          <b-button v-if="canEdit" size="sm" variant="info" @click="openAdd(config)">
            <i class="fa fa-plus mr-1"></i>
            Add
          </b-button>
        </div>

        <div class="reference-list">
          <div v-for="row in filteredRows(config)" :key="config.key + '-' + row.uid" class="reference-entry-wrap">
            <div class="reference-entry" :class="{ 'reference-entry-grouped': config.grouped }">
              <button
                v-if="config.grouped"
                type="button"
                class="reference-expand-button"
                @click="toggleGroup(row.uid)"
              >
                <i class="fa" :class="groupExpanded(row.uid) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </button>

              <div class="reference-entry-main">
                <div class="reference-entry-key" v-html="highlightGroupedText(row.displayKey || 'Untitled', config)"></div>
                <div v-if="config.type !== 'list' || config.grouped" class="reference-entry-value">
                  <span v-html="highlightGroupedText(row.displayValue, config)"></span>
                </div>
              </div>

              <div v-if="canEdit" class="reference-entry-actions">
                <b-button
                  v-if="config.grouped"
                  size="sm"
                  variant="outline-success"
                  v-b-tooltip.hover
                  :title="'Add ' + config.childLabel"
                  @click="openAddGroupedValue(config, row)"
                >
                  <i class="fa fa-plus"></i>
                </b-button>
                <b-button
                  v-if="isRowEditable(config)"
                  size="sm"
                  variant="outline-info"
                  v-b-tooltip.hover
                  title="Edit"
                  @click="openEdit(config, row)"
                >
                  <i class="fa fa-pencil"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  v-b-tooltip.hover
                  title="Delete"
                  @click="confirmDelete(config, row)"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </div>
            </div>

            <div v-if="config.grouped && rowExpanded(config, row)" class="reference-child-list">
              <div v-for="value in visibleGroupedValues(config, row)" :key="config.key + '-' + row.uid + '-' + value" class="reference-child-row">
                <span v-html="highlightGroupedText(displayTagValue(value, config), config)"></span>
                <b-button
                  v-if="canEdit"
                  size="sm"
                  variant="outline-danger"
                  v-b-tooltip.hover
                  :title="'Delete ' + config.childLabel"
                  @click="confirmDeleteGroupedValue(config, row, value)"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </div>
              <div v-if="visibleGroupedValues(config, row).length === 0" class="reference-child-empty">
                No {{ config.childPlural }} added.
              </div>
            </div>
          </div>

          <div v-if="filteredRows(config).length === 0" class="reference-empty">
            No entries found.
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <b-modal
      v-model="editor.visible"
      :title="editorTitle"
      body-bg-variant="dark"
      body-text-variant="light"
      header-bg-variant="dark"
      header-text-variant="light"
      ok-title="Save"
      ok-variant="success"
      :ok-disabled="!editorValid"
      @ok.prevent="applyEditor"
    >
      <b-alert v-if="editor.error" show variant="danger" class="reference-editor-error">
        {{ editor.error }}
      </b-alert>

      <b-form-group v-if="showEditorKeyField" :label="editorConfig ? editorConfig.keyLabel : ''">
        <div
          v-if="editorConfig && editorConfig.keyLookup"
          class="reference-autocomplete"
        >
          <reference-lookup-input
            :min-len="lookupMinLen('key')"
            :options="lookupOptionsForField('key')"
            :value="editor.keyInput"
            :placeholder="editorConfig ? editorConfig.keyLabel : ''"
            @dirty="clearLookupSelection('key')"
            @selected="onKeyLookupSelected"
          ></reference-lookup-input>
        </div>
        <b-form-input
          v-else
          v-model.trim="editor.key"
          class="bg-dark text-white border-secondary"
          :placeholder="editorConfig ? editorConfig.keyLabel : ''"
          @input="clearEditorError"
        ></b-form-input>
      </b-form-group>

      <b-form-group v-if="editorConfig && editorConfig.grouped && editor.mode === 'addValue'" :label="editorConfig.valueLabel">
        <div
          v-if="editorConfig.valueLookup"
          class="reference-autocomplete"
        >
          <reference-lookup-input
            :min-len="lookupMinLen('value')"
            :options="lookupOptionsForField('value')"
            :value="editor.valueInput"
            :placeholder="editorConfig.valueLabel"
            @dirty="clearLookupSelection('value')"
            @selected="onValueLookupSelected"
          ></reference-lookup-input>
        </div>
        <b-form-input
          v-else
          v-model.trim="editor.value"
          class="bg-dark text-white border-secondary"
          :placeholder="editorConfig.valueLabel"
          @input="clearEditorError"
        ></b-form-input>
      </b-form-group>

      <b-form-group v-else-if="editorConfig && !editorConfig.grouped && editorConfig.type === 'text'" :label="editorConfig.valueLabel">
        <div
          v-if="editorConfig.valueLookup"
          class="reference-autocomplete"
        >
          <reference-lookup-input
            :min-len="lookupMinLen('value')"
            :options="lookupOptionsForField('value')"
            :value="editor.valueInput"
            :placeholder="editorConfig.valueLabel"
            @dirty="clearLookupSelection('value')"
            @selected="onValueLookupSelected"
          ></reference-lookup-input>
        </div>
        <b-form-input
          v-else
          v-model.trim="editor.value"
          class="bg-dark text-white border-secondary"
          :placeholder="editorConfig.valueLabel"
          @input="clearEditorError"
        ></b-form-input>
      </b-form-group>

      <b-form-group v-else-if="editorConfig && !editorConfig.grouped && editorConfig.type === 'number'" :label="editorConfig.valueLabel">
        <b-form-input
          v-model.number="editor.value"
          type="number"
          step="0.0001"
          class="bg-dark text-white border-secondary"
          :placeholder="editorConfig.valueLabel"
          @input="clearEditorError"
        ></b-form-input>
      </b-form-group>

      <b-form-group v-else-if="editorConfig && !editorConfig.grouped && editorConfig.type === 'tags'" :label="editorConfig.valueLabel">
        <div v-if="usesTagAutocomplete(editorConfig)" class="reference-tag-selector">
          <div class="reference-chip-list">
            <span
              v-for="value in editor.values"
              :key="'editor-value-' + value"
              class="reference-chip"
            >
              {{ displayTagValue(value, editorConfig) }}
              <button type="button" @click="removeLookupValue(value)">
                <i class="fa fa-times"></i>
              </button>
            </span>
            <span v-if="editor.values.length === 0" class="text-muted small">No {{ editorConfig.valueLabel }} selected.</span>
          </div>
          <div class="reference-tag-input-row">
            <div class="reference-autocomplete">
              <reference-lookup-input
                :min-len="lookupMinLen('tag')"
                :options="lookupOptionsForField('tag')"
                :value="editor.tagInput"
                :placeholder="'Add ' + editorConfig.valueLabel"
                @dirty="clearLookupSelection('tag')"
                @selected="onTagLookupSelected"
              ></reference-lookup-input>
            </div>
            <b-button
              size="sm"
              variant="info"
              class="reference-tag-add-button"
              :disabled="!pendingTagLookupOption"
              @click="addTagLookupInput"
            >
              <i class="fa fa-plus mr-1"></i>
              Add
            </b-button>
          </div>
        </div>
        <b-form-tags
          v-else
          v-model="editor.values"
          separator=","
          tag-variant="info"
          tag-pills
          remove-on-delete
          class="reference-tags bg-dark text-white border-secondary"
          :placeholder="editorConfig.valueLabel"
          @input="clearEditorError"
        ></b-form-tags>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ReferenceLookupInput from './ReferenceLookupInput.vue'

const ALLOWED_TIER_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '9.5', '10']
const ALLOWED_NATURE_OPTIONS = [
  'Hardy',
  'Lonely',
  'Brave',
  'Adamant',
  'Naughty',
  'Bold',
  'Docile',
  'Relaxed',
  'Impish',
  'Lax',
  'Timid',
  'Hasty',
  'Serious',
  'Jolly',
  'Naive',
  'Modest',
  'Mild',
  'Quiet',
  'Bashful',
  'Rash',
  'Calm',
  'Gentle',
  'Sassy',
  'Careful',
  'Quirky'
]

const CONFIGS = [
  { key: 'pokemonRarityTiers', title: 'Pre-defined Pokemon Tiers', keyLabel: 'Pokemon', valueLabel: 'Tiers', type: 'tags', keyLookup: 'pokemon', valueOptions: ALLOWED_TIER_OPTIONS, valueNormalizer: 'tier', mergeDuplicateKey: true },
  { key: 'preferredPokemonNatures', title: 'Preferred Pokemon Natures', keyLabel: 'Pokemon', valueLabel: 'Natures', type: 'tags', keyLookup: 'pokemon', valueOptions: ALLOWED_NATURE_OPTIONS, valueNormalizer: 'nature' },
  { key: 'itemDrops', title: 'Pre-defined Item Drops', keyLabel: 'Pokemon', valueLabel: 'Item', type: 'text', keyLookup: 'pokemon', valueLookup: 'items', uniqueKey: true },
  { key: 'itemDropChances', title: 'Pre-defined Item Drop Chances', keyLabel: 'Item', valueLabel: 'Drop Chance (%)', type: 'number', keyLookup: 'items', valueSuffix: '%', uniqueKey: true },
  { key: 'selfHarmingMoves', title: 'Self-harming Moves', keyLabel: 'Move', valueLabel: '', type: 'list', keyLookup: 'skills', uniqueKey: true, editable: false },
  { key: 'mapRegions', title: 'Map Regions', keyLabel: 'Region', valueLabel: 'Map', type: 'tags', grouped: true, childLabel: 'Map', childPlural: 'maps', uniqueGroupedValues: true },
  { key: 'habitats', title: 'Habitats', keyLabel: 'Habitat', valueLabel: 'Pokemon', type: 'tags', valueLookup: 'pokemon', grouped: true, childLabel: 'Pokemon', childPlural: 'Pokemon' }
]

let rowUid = 1

export default {
  name: 'SpawnReferenceData',
  components: {
    ReferenceLookupInput
  },
  data () {
    return {
      activeTabIndex: 0,
      configs: CONFIGS,
      editor: {
        visible: false,
        mode: 'add',
        configKey: '',
        parentUid: null,
        uid: null,
        key: '',
        keyInput: '',
        value: '',
        valueInput: '',
        tagInput: '',
        tagValue: '',
        values: [],
        error: ''
      },
      loading: false,
      lookupOptionGroups: Object.create(null),
      lookupOptionIndexes: Object.create(null),
      expandedGroups: {},
      rows: {},
      saving: false,
      search: {},
      searchDebounceTimers: {},
      searchInputs: {}
    }
  },
  computed: {
    ...mapGetters({
      referenceData: 'spawnReferenceData',
      referenceLookups: 'spawnReferenceLookups',
      referenceDataUpdatedAt: 'spawnReferenceDataUpdatedAt',
      serverCanEdit: 'spawnCanEditReferenceData'
    }),
    canEdit () {
      return this.serverCanEdit || this.hasPermission('SpawnsReferenceDataEdit')
    },
    editorConfig () {
      return this.configs.find(config => config.key === this.editor.configKey) || null
    },
    editorTitle () {
      if (!this.editorConfig) return 'Reference Data'
      if (this.editor.mode === 'addValue') {
        return 'Add ' + this.editorConfig.childLabel + ' to ' + this.editor.key
      }
      return (this.editor.mode === 'edit' ? 'Edit ' : 'Add ') + this.editorConfig.title
    },
    showEditorKeyField () {
      return !!this.editorConfig && this.editor.mode !== 'addValue'
    },
    editorValid () {
      if (!this.editorConfig) return false
      if (this.editorConfig.grouped && this.editor.mode === 'addValue') {
        return !!this.normalizedEditorField('value')
      }
      if (!this.normalizedEditorField('key')) return false
      if (this.editorConfig.grouped) return true
      if (this.editorConfig.type === 'list') return true
      if (this.editorConfig.type === 'tags') return this.cleanReferenceValueList(this.editorConfig, this.editor.values || []).length > 0
      if (this.editorConfig.type === 'number') return Number.isFinite(Number(this.editor.value))
      return !!this.normalizedEditorField('value')
    },
    pendingTagLookupOption () {
      if (!this.editor.tagValue) return null
      return this.findFieldOption('tag', this.editor.tagValue)
    }
  },
  watch: {
    referenceLookups: {
      immediate: true,
      handler () {
        this.rebuildLookupCache()
      }
    },
    referenceData: {
      immediate: true,
      handler (data) {
        this.rows = this.rowsFromData(data || {})
      }
    },
    referenceDataUpdatedAt () {
      this.loading = false
      this.saving = false
    }
  },
  created () {
    CONFIGS.forEach(config => {
      this.$set(this.search, config.key, '')
      this.$set(this.searchInputs, config.key, '')
      if (!this.rows[config.key]) {
        this.$set(this.rows, config.key, [])
      }
    })
    this.refresh()
  },
  beforeDestroy () {
    Object.keys(this.searchDebounceTimers).forEach(key => {
      window.clearTimeout(this.searchDebounceTimers[key])
    })
  },
  methods: {
    rebuildLookupCache () {
      const groups = ['pokemon', 'items', 'skills'].reduce((result, lookupKey) => {
        const source = this.referenceLookups && this.referenceLookups[lookupKey] ? this.referenceLookups[lookupKey] : {}
        result[lookupKey] = Object.keys(source)
          .map(id => {
            const name = String(source[id] || '').trim()
            return {
              id: String(id),
              name,
              label: name,
              search: name.toLowerCase()
            }
          })
          .filter(option => option.name)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
        return result
      }, Object.create(null))

      const indexes = Object.keys(groups).reduce((result, lookupKey) => {
        const byID = Object.create(null)
        const byLabel = Object.create(null)
        ;(groups[lookupKey] || []).forEach(option => {
          byID[String(option.id).toLowerCase()] = option
          byLabel[String(option.label).toLowerCase()] = option
          byLabel[String(option.name).toLowerCase()] = option
        })
        result[lookupKey] = { byID, byLabel }
        return result
      }, Object.create(null))

      this.lookupOptionGroups = groups
      this.lookupOptionIndexes = indexes
    },
    queueSearch (key, value) {
      if (this.searchDebounceTimers[key]) {
        window.clearTimeout(this.searchDebounceTimers[key])
      }
      this.searchDebounceTimers[key] = window.setTimeout(() => {
        this.$set(this.search, key, String(value || '').trim().toLowerCase())
        this.$delete(this.searchDebounceTimers, key)
      }, 160)
    },
    groupExpanded (uid) {
      return !!this.expandedGroups[uid]
    },
    toggleGroup (uid) {
      this.$set(this.expandedGroups, uid, !this.groupExpanded(uid))
    },
    refresh () {
      this.loading = true
      this.emit('getSpawnReferenceData', { referenceOnly: true })
    },
    openAdd (config) {
      this.editor = {
        visible: true,
        mode: 'add',
        configKey: config.key,
        parentUid: null,
        uid: null,
        key: '',
        keyInput: '',
        value: config.type === 'number' ? 0 : '',
        valueInput: '',
        tagInput: '',
        tagValue: '',
        values: [],
        error: ''
      }
    },
    openEdit (config, row) {
      if (!this.isRowEditable(config)) return
      this.editor = {
        visible: true,
        mode: 'edit',
        configKey: config.key,
        parentUid: null,
        uid: row.uid,
        key: row.key,
        keyInput: row.displayKey || this.displayWithLookup(row.key, config.keyLookup),
        value: row.value,
        valueInput: row.displayValue || this.displayWithLookup(row.value, config.valueLookup),
        tagInput: '',
        tagValue: '',
        values: Array.isArray(row.values) ? row.values.slice() : [],
        error: ''
      }
    },
    openAddGroupedValue (config, row) {
      this.editor = {
        visible: true,
        mode: 'addValue',
        configKey: config.key,
        parentUid: row.uid,
        uid: null,
        key: row.key,
        keyInput: row.displayKey || row.key,
        value: '',
        valueInput: '',
        tagInput: '',
        tagValue: '',
        values: [],
        error: ''
      }
    },
    applyEditor () {
      if (!this.editorValid || !this.editorConfig) return
      this.clearEditorError()
      if (this.editor.mode === 'addValue') {
        this.applyGroupedValue()
        return
      }
      const row = this.decorateRow(this.editorConfig, this.editorRow())
      const duplicateError = this.uniqueRowError(this.editorConfig, row)
      if (duplicateError) {
        this.setEditorError(duplicateError)
        return
      }
      if (this.mergeDuplicateRow(this.editorConfig, row)) {
        return
      }
      if (this.editor.mode === 'edit') {
        const index = this.rows[this.editorConfig.key].findIndex(candidate => candidate.uid === this.editor.uid)
        if (index !== -1) {
          this.$set(this.rows[this.editorConfig.key], index, row)
        }
      } else if (this.editorConfig.grouped) {
        const existingIndex = this.rows[this.editorConfig.key].findIndex(candidate => {
          return String(candidate.key || '').toLowerCase() === String(row.key || '').toLowerCase()
        })
        if (existingIndex === -1) {
          this.rows[this.editorConfig.key].unshift(row)
          this.$set(this.expandedGroups, row.uid, true)
        } else {
          this.$set(this.expandedGroups, this.rows[this.editorConfig.key][existingIndex].uid, true)
        }
      } else {
        this.rows[this.editorConfig.key].unshift(row)
      }
      this.editor.visible = false
      if (!this.editorConfig.grouped || row.values.length || this.editor.mode === 'edit') {
        this.save()
      }
    },
    applyGroupedValue () {
      this.clearEditorError()
      const value = this.normalizedEditorField('value')
      const index = this.rows[this.editorConfig.key].findIndex(candidate => candidate.uid === this.editor.parentUid)
      if (!value || index === -1) return
      const duplicateError = this.uniqueGroupedValueError(this.editorConfig, value)
      if (duplicateError) {
        this.setEditorError(duplicateError)
        return
      }
      const row = Object.assign({}, this.rows[this.editorConfig.key][index])
      const values = this.cleanStringList((row.values || []).concat([value]))
      row.values = values
      this.$set(this.rows[this.editorConfig.key], index, this.decorateRow(this.editorConfig, row))
      this.$set(this.expandedGroups, row.uid, true)
      this.editor.visible = false
      this.save()
    },
    isRowEditable (config) {
      return !config || config.editable !== false
    },
    uniqueRowError (config, row) {
      if (!config || !config.uniqueKey) return ''
      const duplicate = this.findDuplicateKeyRow(config, row.key, this.editor.mode === 'edit' ? this.editor.uid : null)
      if (!duplicate) return ''
      return config.keyLabel + ' "' + (duplicate.displayKey || this.displayKey(config, row)) + '" already exists in ' + config.title + '.'
    },
    findDuplicateKeyRow (config, key, ignoreUid) {
      const normalizedKey = this.normalizedKeyForCompare(key)
      if (!normalizedKey) return null
      return (this.rows[config.key] || []).find(candidate => {
        return candidate.uid !== ignoreUid && this.normalizedKeyForCompare(candidate.key) === normalizedKey
      }) || null
    },
    mergeDuplicateRow (config, row) {
      if (!config || !config.mergeDuplicateKey || this.editor.mode !== 'add') return false
      const existing = this.findDuplicateKeyRow(config, row.key, null)
      if (!existing) return false
      const mergedValues = this.cleanReferenceValueList(config, (existing.values || []).concat(row.values || []))
      if (this.sameStringList(mergedValues, existing.values || [])) {
        this.setEditorError((existing.displayKey || this.displayKey(config, existing)) + ' already has the selected ' + config.valueLabel + '.')
        return true
      }
      const index = this.rows[config.key].findIndex(candidate => candidate.uid === existing.uid)
      if (index === -1) return false
      this.$set(this.rows[config.key], index, this.decorateRow(config, Object.assign({}, existing, { values: mergedValues })))
      this.editor.visible = false
      this.save()
      return true
    },
    uniqueGroupedValueError (config, value) {
      if (!config || !config.uniqueGroupedValues) return ''
      const normalizedValue = this.normalizedKeyForCompare(value)
      if (!normalizedValue) return ''
      const duplicate = (this.rows[config.key] || []).find(row => {
        return (row.values || []).some(candidate => this.normalizedKeyForCompare(candidate) === normalizedValue)
      })
      if (!duplicate) return ''
      return config.childLabel + ' "' + this.displayTagValue(value, config) + '" already exists in ' + config.title + ' under "' + duplicate.displayKey + '".'
    },
    normalizedKeyForCompare (value) {
      return String(value || '').trim().toLowerCase()
    },
    sameStringList (left, right) {
      const cleanLeft = (left || []).map(value => String(value || '')).sort()
      const cleanRight = (right || []).map(value => String(value || '')).sort()
      if (cleanLeft.length !== cleanRight.length) return false
      return cleanLeft.every((value, index) => value === cleanRight[index])
    },
    setEditorError (message) {
      this.$set(this.editor, 'error', message)
    },
    clearEditorError () {
      if (this.editor.error) {
        this.$set(this.editor, 'error', '')
      }
    },
    editorRow () {
      const key = this.normalizedEditorField('key')
      if (this.editorConfig.type === 'tags') {
        return {
          uid: this.editor.uid || rowUid++,
          key,
          values: this.cleanReferenceValueList(this.editorConfig, this.editor.values || [])
        }
      }
      return {
        uid: this.editor.uid || rowUid++,
        key,
        value: this.editorConfig.type === 'number' ? Number(this.editor.value) : this.normalizedEditorField('value')
      }
    },
    confirmDelete (config, row) {
      if (!row) return
      this.$bvModal.msgBoxConfirm('Delete "' + row.displayKey + '" from ' + config.title + '?', {
        title: 'Delete entry',
        okTitle: 'Delete',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        bodyBgVariant: 'dark',
        bodyTextVariant: 'light',
        headerBgVariant: 'dark',
        headerTextVariant: 'light'
      }).then(confirmed => {
        if (!confirmed) return
        const index = this.rows[config.key].findIndex(candidate => candidate.uid === row.uid)
        if (index !== -1) {
          this.rows[config.key].splice(index, 1)
          this.save()
        }
      })
    },
    confirmDeleteGroupedValue (config, row, value) {
      const label = this.displayWithLookup(value, config.valueLookup)
      this.$bvModal.msgBoxConfirm('Delete "' + label + '" from ' + row.displayKey + '?', {
        title: 'Delete ' + config.childLabel,
        okTitle: 'Delete',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        bodyBgVariant: 'dark',
        bodyTextVariant: 'light',
        headerBgVariant: 'dark',
        headerTextVariant: 'light'
      }).then(confirmed => {
        if (!confirmed) return
        const index = this.rows[config.key].findIndex(candidate => candidate.uid === row.uid)
        if (index === -1) return
        const nextRow = Object.assign({}, this.rows[config.key][index], {
          values: (this.rows[config.key][index].values || []).filter(candidate => candidate !== value)
        })
        this.$set(this.rows[config.key], index, this.decorateRow(config, nextRow))
        this.save()
      })
    },
    save () {
      if (!this.canEdit) return
      this.saving = true
      this.emit('saveSpawnReferenceData', this.dataFromRows())
    },
    rowsFromData (data) {
      const rows = {}
      CONFIGS.forEach(config => {
        if (config.type === 'list') {
          rows[config.key] = this.arrayFrom(data[config.key]).map(value => this.decorateRow(config, {
            uid: rowUid++,
            key: value,
            value: ''
          }))
        } else if (config.type === 'tags') {
          rows[config.key] = Object.keys(data[config.key] || {})
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map(key => this.decorateRow(config, {
              uid: rowUid++,
              key,
              values: this.arrayFrom(data[config.key][key])
            }))
        } else {
          rows[config.key] = Object.keys(data[config.key] || {})
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map(key => this.decorateRow(config, {
              uid: rowUid++,
              key,
              value: data[config.key][key]
            }))
        }
      })
      return rows
    },
    decorateRow (config, row) {
      const next = Object.assign({}, row)
      next.displayKey = this.displayKey(config, next)
      next.displayValue = this.displayValue(config, next)
      next.searchText = this.rowSearchText(config, next)
      return next
    },
    dataFromRows () {
      const data = {}
      CONFIGS.forEach(config => {
        if (config.type === 'list') {
          data[config.key] = this.cleanStringList((this.rows[config.key] || []).map(row => row.key))
          return
        }
        data[config.key] = {}
        ;(this.rows[config.key] || []).forEach(row => {
          const key = String(row.key || '').trim()
          if (!key) return
          if (config.type === 'tags') {
            const values = this.cleanReferenceValueList(config, row.values || [])
            if (values.length) {
              data[config.key][key] = values
            }
          } else if (config.type === 'number') {
            const value = Number(row.value)
            if (Number.isFinite(value)) {
              data[config.key][key] = value
            }
          } else {
            const value = this.normalizeReferenceValue(config, row.value)
            if (value) {
              data[config.key][key] = value
            }
          }
        })
      })
      return data
    },
    filteredRows (config) {
      const rows = this.rows[config.key] || []
      const search = String(this.search[config.key] || '').toLowerCase()
      if (!search) return rows
      return rows.filter(row => this.rowMatchesSearch(config, row, search))
    },
    rowMatchesSearch (config, row, search) {
      if (String(row.searchText || '').indexOf(search) !== -1) return true
      if (!config.grouped || config.type !== 'tags') return false
      return (row.values || []).some(value => this.displayTagValue(value, config).toLowerCase().indexOf(search) !== -1)
    },
    rowExpanded (config, row) {
      if (!config.grouped) return false
      if (this.groupExpanded(row.uid)) return true
      const search = String(this.search[config.key] || '').toLowerCase()
      return !!search && this.rowMatchesSearch(config, row, search)
    },
    visibleGroupedValues (config, row) {
      const values = row.values || []
      const search = String(this.search[config.key] || '').toLowerCase()
      if (!search) return values
      const matchingValues = values.filter(value => this.displayTagValue(value, config).toLowerCase().indexOf(search) !== -1)
      if (matchingValues.length) return matchingValues
      return String(row.displayKey || '').toLowerCase().indexOf(search) !== -1 ? values : []
    },
    displayKey (config, row) {
      return this.displayWithLookup(row.key, config.keyLookup)
    },
    displayValue (config, row) {
      if (config.grouped) return this.groupedCountLabel(config, row)
      if (config.type === 'tags') return (row.values || []).map(value => this.displayTagValue(value, config)).join(', ')
      if (config.type === 'number') return String(row.value) + (config.valueSuffix || '')
      return this.displayWithLookup(row.value, config.valueLookup)
    },
    displayTagValue (value, config) {
      const option = this.findStaticOption(config, value)
      if (option) return option.label
      return this.displayWithLookup(value, config && config.valueLookup)
    },
    displayWithLookup (value, lookupKey) {
      const id = String(value || '').trim()
      if (!id || !lookupKey) return id
      const lookups = this.referenceLookups && this.referenceLookups[lookupKey] ? this.referenceLookups[lookupKey] : {}
      const name = lookups[id] ? String(lookups[id]).trim() : ''
      if (name) return name
      if (lookupKey === 'pokemon') return 'Unknown Pokemon'
      if (lookupKey === 'items') return 'Unknown item'
      if (lookupKey === 'skills') return 'Unknown move'
      return id
    },
    groupedCountLabel (config, row) {
      const count = (row.values || []).length
      return count + ' ' + (count === 1 ? config.childLabel : config.childPlural)
    },
    rowSearchText (config, row) {
      const parts = [row.displayKey || '', row.displayValue || '']
      if (!config.grouped && config.type !== 'list') {
        parts.push(String(row.key || ''), String(row.value || ''))
      }
      if (!config.grouped && config.type === 'tags') {
        ;(row.values || []).forEach(value => {
          parts.push(this.displayTagValue(value, config))
        })
      } else if (config.grouped && config.type === 'tags') {
        ;(row.values || []).forEach(value => {
          parts.push(this.displayTagValue(value, config))
        })
      }
      return parts.join(' ').toLowerCase()
    },
    usesTagAutocomplete (config) {
      return !!(config && (config.valueLookup || config.valueOptions))
    },
    onKeyLookupSelected (item) {
      this.onLookupSelected('key', item)
    },
    onValueLookupSelected (item) {
      this.onLookupSelected('value', item)
    },
    onTagLookupSelected (item) {
      this.onLookupSelected('tag', item)
    },
    onLookupSelected (field, item) {
      if (!item) return
      this.clearEditorError()
      const option = this.findFieldOption(field, this.lookupChangeText(item))
      if (!option) return
      if (field === 'tag') {
        this.editor.tagValue = option.id
        this.editor.tagInput = option.label
      } else {
        this.setLookupValue(field, option.id)
        this.setLookupInput(field, option.label)
        if (field === 'key') {
          this.prefillMergeValuesForDuplicateKey()
        }
      }
    },
    addTagLookupInput () {
      const option = this.pendingTagLookupOption
      if (!option) return
      this.clearEditorError()
      this.addLookupValue(option.id)
      this.editor.tagInput = ''
      this.editor.tagValue = ''
    },
    clearLookupSelection (field) {
      this.clearEditorError()
      if (field === 'key') this.editor.key = ''
      if (field === 'value') this.editor.value = ''
      if (field === 'tag') this.editor.tagValue = ''
    },
    prefillMergeValuesForDuplicateKey () {
      if (!this.editorConfig || !this.editorConfig.mergeDuplicateKey || this.editor.mode !== 'add') return
      const key = this.normalizedEditorField('key')
      const existing = this.findDuplicateKeyRow(this.editorConfig, key, null)
      if (!existing) return
      this.editor.values = this.cleanReferenceValueList(this.editorConfig, (existing.values || []).concat(this.editor.values || []))
    },
    lookupChangeText (item) {
      if (item && item.target && typeof item.target.value === 'string') return item.target.value
      if (item && item.label) return item.label
      if (item !== null && item !== undefined) return String(item)
      return ''
    },
    lookupOptionsForField (field) {
      const lookupKey = this.lookupKeyForField(field)
      if (lookupKey) return this.lookupOptionGroups[lookupKey] || []
      if ((field === 'value' || field === 'tag') && this.editorConfig && this.editorConfig.valueOptions) {
        return this.staticOptionsForConfig(this.editorConfig)
      }
      return []
    },
    lookupMinLen (field) {
      const lookupKey = this.lookupKeyForField(field)
      return lookupKey ? 2 : 1
    },
    lookupKeyForField (field) {
      if (!this.editorConfig) return ''
      if (field === 'key') return this.editorConfig.keyLookup || ''
      return this.editorConfig.valueLookup || ''
    },
    findFieldOption (field, value) {
      const lookupKey = this.lookupKeyForField(field)
      if (lookupKey) return this.findLookupOption(lookupKey, value)
      if ((field === 'value' || field === 'tag') && this.editorConfig) {
        return this.findStaticOption(this.editorConfig, value)
      }
      return null
    },
    setLookupInput (field, value) {
      const text = String(value || '')
      if (field === 'key') this.editor.keyInput = text
      if (field === 'value') this.editor.valueInput = text
      if (field === 'tag') this.editor.tagInput = text
    },
    setLookupValue (field, value) {
      const id = String(value || '').trim()
      if (field === 'key') this.editor.key = id
      if (field === 'value') this.editor.value = id
    },
    addLookupValue (value) {
      const id = String(value || '').trim()
      if (!id || this.editor.values.indexOf(id) !== -1) return
      this.editor.values.push(id)
    },
    removeLookupValue (value) {
      this.clearEditorError()
      this.editor.values = this.editor.values.filter(candidate => candidate !== value)
    },
    normalizedEditorField (field) {
      const lookupKey = this.lookupKeyForField(field)
      const current = String(field === 'key' ? this.editor.key : this.editor.value || '').trim()
      const input = String(field === 'key' ? this.editor.keyInput : this.editor.valueInput || '').trim()
      if (lookupKey) {
        return current ? this.resolveLookupID(lookupKey, current) : ''
      }
      if (!lookupKey) {
        if ((field === 'value' || field === 'tag') && this.editorConfig && this.editorConfig.valueOptions) {
          const option = this.findStaticOption(this.editorConfig, input || current)
          return option ? option.id : ''
        }
        return String(field === 'key' ? this.editor.key : this.editor.value || '').trim()
      }
      return ''
    },
    resolveLookupID (lookupKey, value) {
      const text = String(value || '').trim()
      if (!text) return ''
      const numericMatch = /^#?(\d+)$/.exec(text) || /\(#(\d+)\)$/.exec(text)
      if (numericMatch && this.lookupExists(lookupKey, numericMatch[1])) return String(Number(numericMatch[1]))
      const option = this.findLookupOption(lookupKey, text)
      if (option) return option.id
      return ''
    },
    lookupExists (lookupKey, id) {
      const lookups = this.referenceLookups && this.referenceLookups[lookupKey] ? this.referenceLookups[lookupKey] : {}
      return !!lookups[String(Number(id))]
    },
    findLookupOption (lookupKey, value) {
      const text = String(value || '').trim().toLowerCase()
      if (!text) return null
      const stripped = text.replace(/\s*\(#\d+\)\s*$/, '')
      const index = this.lookupOptionIndexes[lookupKey] || {}
      return (index.byID && index.byID[text]) ||
        (index.byLabel && index.byLabel[text]) ||
        (index.byLabel && index.byLabel[stripped]) ||
        null
    },
    staticOptionsForConfig (config) {
      if (!config || !Array.isArray(config.valueOptions)) return []
      return config.valueOptions.map(value => {
        const id = this.normalizeReferenceValue(config, value)
        const label = String(value || '').trim()
        return {
          id,
          name: label,
          label,
          search: (label + ' ' + id).toLowerCase()
        }
      }).filter(option => option.id && option.label)
    },
    findStaticOption (config, value) {
      if (!config || !Array.isArray(config.valueOptions)) return null
      const text = String(value || '').trim().toLowerCase()
      if (!text) return null
      return this.staticOptionsForConfig(config).find(option => {
        return option.id.toLowerCase() === text ||
          option.label.toLowerCase() === text ||
          option.name.toLowerCase() === text
      }) || null
    },
    normalizeReferenceValue (config, value) {
      const text = String(value || '').trim()
      if (!text) return ''
      if (config && config.valueNormalizer === 'tier') return this.normalizeTierValue(text)
      if (config && config.valueNormalizer === 'nature') return this.normalizeNatureValue(text)
      if (config && config.valueLookup) return this.resolveLookupID(config.valueLookup, text)
      return text
    },
    normalizeTierValue (value) {
      const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/^tier\s+/, '')
        .replace(/^t/, '')
      if (ALLOWED_TIER_OPTIONS.indexOf(normalized) === -1) return ''
      return 'T' + normalized
    },
    normalizeNatureValue (value) {
      const text = String(value || '').trim().toLowerCase()
      const match = ALLOWED_NATURE_OPTIONS.find(nature => nature.toLowerCase() === text)
      return match || ''
    },
    cleanReferenceValueList (config, values) {
      const seen = Object.create(null)
      return values
        .map(value => this.normalizeReferenceValue(config, value))
        .filter(value => value)
        .filter(value => {
          const key = value.toLowerCase()
          if (seen[key]) return false
          seen[key] = true
          return true
        })
    },
    highlightGroupedText (value, config) {
      const search = config ? String(this.search[config.key] || '').trim() : ''
      if (!search) return this.escapeHtml(value)
      return this.highlightText(value, search)
    },
    highlightText (value, search) {
      const text = String(value || '')
      const needle = String(search || '').trim()
      if (!needle) return this.escapeHtml(text)
      const lowerText = text.toLowerCase()
      const lowerNeedle = needle.toLowerCase()
      const index = lowerText.indexOf(lowerNeedle)
      if (index === -1) return this.escapeHtml(text)
      return this.escapeHtml(text.slice(0, index)) +
        '<span class="reference-highlight">' + this.escapeHtml(text.slice(index, index + needle.length)) + '</span>' +
        this.escapeHtml(text.slice(index + needle.length))
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    arrayFrom (value) {
      return Array.isArray(value) ? value.slice() : []
    },
    cleanStringList (values) {
      const seen = Object.create(null)
      return values
        .map(value => String(value || '').trim())
        .filter(value => value)
        .filter(value => {
          const key = value.toLowerCase()
          if (seen[key]) return false
          seen[key] = true
          return true
        })
    }
  }
}
</script>

<style scoped>
.spawn-reference-data {
  color: #e6edf3;
}

.spawn-reference-data button:focus,
.spawn-reference-data .btn:focus,
.spawn-reference-data .btn.focus,
.spawn-reference-data /deep/ .btn:focus,
.spawn-reference-data /deep/ .nav-link:focus {
  outline: 0;
  box-shadow: none;
}

.reference-toolbar-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #1f252b;
  border-radius: 8px;
  margin-bottom: 14px;
}

.reference-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
}

.reference-title-row,
.reference-actions,
.reference-section-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reference-title-row {
  font-size: 1rem;
}

.reference-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.reference-tabs {
  background: transparent;
}

.reference-section-bar {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.reference-search {
  max-width: 360px;
}

.reference-list {
  max-height: calc(100vh - 236px);
  min-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.reference-entry-wrap {
  margin-bottom: 5px;
}

.reference-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  background: #1b2026;
  padding: 6px 8px;
}

.reference-entry:hover {
  border-color: rgba(32, 168, 216, 0.36);
  background: #202832;
}

.reference-entry-main {
  flex: 1 1 auto;
  min-width: 0;
}

.reference-entry-key {
  color: #fff;
  font-weight: 800;
  line-height: 1.2;
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}

.reference-entry-value {
  color: #aeb9c5;
  font-size: 0.78rem;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: min(980px, 62vw);
}

.reference-entry-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.reference-expand-button {
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #9fb0bf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  padding: 0;
}

.reference-expand-button:hover {
  background: #26313b;
  color: #fff;
}

.reference-child-list {
  border: 1px solid rgba(32, 168, 216, 0.22);
  border-top: 0;
  border-radius: 0 0 6px 6px;
  background: #171d23;
  margin: -1px 0 0 32px;
  padding: 5px;
}

.reference-child-row {
  min-height: 31px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 4px;
  color: #d7dee6;
  padding: 3px 4px 3px 8px;
}

.reference-child-row:hover {
  background: #202832;
}

.reference-child-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reference-child-empty {
  color: #8f9baa;
  font-size: 0.8rem;
  padding: 8px;
}

.reference-tags {
  min-height: 35px;
}

.reference-autocomplete {
  position: relative;
}

.reference-editor-error {
  margin-bottom: 12px;
}

.reference-highlight {
  background: rgba(32, 168, 216, 0.28);
  border-radius: 3px;
  color: #fff;
  padding: 0 2px;
}

.reference-tag-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reference-tag-input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
}

.reference-tag-add-button {
  min-width: 72px;
  min-height: 35px;
  white-space: nowrap;
}

.reference-chip-list {
  min-height: 35px;
  border: 1px solid #3e515b;
  border-radius: 4px;
  background: #2f353a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 5px 7px;
}

.reference-chip {
  border-radius: 999px;
  background: #20a8d8;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 4px 8px;
}

.reference-chip button {
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.reference-empty {
  min-height: 140px;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f9baa;
  margin-top: 10px;
}

@media (max-width: 767.98px) {
  .reference-toolbar,
  .reference-section-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .reference-actions,
  .reference-search {
    width: 100%;
  }

  .reference-entry {
    align-items: flex-start;
    flex-direction: column;
  }

  .reference-entry-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .reference-child-list {
    margin-left: 0;
  }

  .reference-child-row span {
    white-space: normal;
  }
}
</style>
